import { Cta } from "@/components/Cta";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/lib/seo";
import { Card, Container, Grid, SimpleGrid, Stack, Text, Title } from "@repo/ui/core";
import {
  IconArrowRight,
  IconBrandDocker,
  IconCheck,
  IconCloud,
  IconCode,
  IconGitBranch,
  IconPackage,
  IconTestPipe,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const capabilities = [
  {
    title: "GitLab Integration",
    description:
      "Integrates with GitLab Dedicated and SaaS. Use your existing workflows, pipelines, and registries.",
    icon: IconGitBranch,
  },
  {
    title: "Multi-Cloud Infrastructure",
    description:
      "Integrated with AWS and GCP. Billing passes through to your account, so spend tracks actual build activity.",
    icon: IconCloud,
  },
  {
    title: "Package Management",
    description:
      "GitLab package registry with future support for Artifactory, CodeArtifact, and Google Artifact Registry.",
    icon: IconPackage,
  },
  {
    title: "Container Registry",
    description:
      "GitLab container registry with planned ECR and Google Artifact Registry support.",
    icon: IconBrandDocker,
  },
  {
    title: "Remote Development",
    description:
      "GitLab Workspaces for browser-based development, with devfiles for reproducible environments.",
    icon: IconCode,
  },
  {
    title: "Automated Testing",
    description:
      "Extensible behavioral testing platform for requirements verification and application testing.",
    icon: IconTestPipe,
  },
];

const androidSupport = [
  "Android 14, 15, and 16",
  "Yocto Linux",
  "x86_64 and arm64, build and emulate",
  "Cloud emulators in the browser",
];

const androidRoadmap = ["QNX"];

const cloudSupport = [
  "AWS and Google Cloud",
  "10+ DNS providers",
  "Billing through your own cloud accounts",
];

const cloudRoadmap = ["Azure"];

function SupportList({ items }: { items: string[] }) {
  return (
    <ul className={styles.checkList}>
      {items.map((item) => (
        <li key={item}>
          <IconCheck size={16} stroke={2.5} />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function Platform() {
  useSeo("/platform");

  return (
    <>
      <PageHero
        eyebrow="Platform"
        title={
          <>
            The complete Android Automotive{" "}
            <span className={styles.gradientText}>platform</span>
          </>
        }
        description="Build, emulate, develop, and test from one control plane, running in your own cloud accounts and wired into your existing GitLab workflow."
      >
        <Cta to="/contact">Book a Demo</Cta>
      </PageHero>

      {/* Capabilities */}
      <Section>
        <SectionHeader
          title="Built for automotive teams"
          description="Every capability runs in your cloud accounts and plugs into the GitLab setup you already have."
        />
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {capabilities.map((item, i) => (
            <Reveal key={item.title} delay={(i % 3) * 0.05}>
              <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
                <Stack gap="md">
                  <span className={styles.iconChip}>
                    <item.icon size={20} stroke={1.5} />
                  </span>
                  <Title order={3} size="1.125rem" fw={600}>
                    {item.title}
                  </Title>
                  <Text c="dimmed" size="sm" lh={1.7}>
                    {item.description}
                  </Text>
                </Stack>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Section>

      {/* Supported platforms */}
      <Section hairline>
        <SectionHeader
          title="Broad platform support"
          description="AAOSP on AWS and Google Cloud today, with more targets on the roadmap."
        />
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
              <Stack gap="lg">
                <Title order={3} size="1.25rem" fw={600}>
                  Build targets
                </Title>
                <SupportList items={androidSupport} />
                <Text size="sm" c="dimmed">
                  On the roadmap: {androidRoadmap.join(", ")}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
              <Stack gap="lg">
                <Title order={3} size="1.25rem" fw={600}>
                  Cloud Providers
                </Title>
                <SupportList items={cloudSupport} />
                <Text size="sm" c="dimmed">
                  On the roadmap: {cloudRoadmap.join(", ")}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title order={2} fw={650} size="2rem" style={{ letterSpacing: "-0.02em" }}>
              See the platform in action
            </Title>
            <Text size="lg" c="dimmed" maw={480} mx="auto" mt="md" mb="xl" lh={1.65}>
              Schedule a personalized walkthrough with our team.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
              <Cta
                to="/products/android-builds"
                variant="ghost"
                iconRight={<IconArrowRight size={16} />}
              >
                Explore Products
              </Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
